import {
  Button,
  Select,
  Space,
  Table,
  TableProps,
  Tag,
  Typography,
  message,
  Modal,
  Input,
  Tabs,
} from 'antd'
import { EyeOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { memo, useCallback, useEffect, useRef, useState } from 'react'

import {
  getTeacherApplications,
  approveTeacherApplication,
  rejectTeacherApplication,
} from '~/services/teacher-application'
import { ITeacherApplication } from '~/types/teacher-application'
import { isAdmin } from '~/utils/auth'
import { getTeachingLanguageSummary } from '~/utils/teaching-language'

import ApplicationDetails from './ApplicationDetails'
import DemoReviewPanel from './DemoReviewPanel'
import { Container } from './style'

const { TextArea } = Input

const Index = () => {
  const [loading, setLoading] = useState(false)
  const [dataSource, setDataSource] = useState<ITeacherApplication[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(20)
  const [status, setStatus] = useState<string>('PENDING')
  const [admin] = useState(isAdmin())

  // 详情弹窗
  const [isDetailsVisible, setIsDetailsVisible] = useState(false)
  const [selectedApplication, setSelectedApplication] = useState<ITeacherApplication | null>(null)

  // 审核弹窗
  const [isApproveVisible, setIsApproveVisible] = useState(false)
  const [isRejectVisible, setIsRejectVisible] = useState(false)
  const [reviewNotes, setReviewNotes] = useState('')
  const [currentApplicationId, setCurrentApplicationId] = useState('')

  const fetchData = useCallback(() => {
    setLoading(true)
    getTeacherApplications({ page, size, status: status || undefined })
      .then((res) => {
        setDataSource(res.data?.applications || [])
        setTotal(res.data?.total || 0)
      })
      .catch((err) => {
        console.log(err)
        message.error(err?.message || '请求错误，请稍后再试')
      })
      .finally(() => {
        setLoading(false)
      })
  }, [page, size, status])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const onSearch = useCallback(() => {
    setPage(1)
    fetchData()
  }, [fetchData])

  const handleViewDetails = (record: ITeacherApplication) => {
    setSelectedApplication(record)
    setIsDetailsVisible(true)
  }

  const handleApprove = (id: string) => {
    setCurrentApplicationId(id)
    setReviewNotes('')
    setIsApproveVisible(true)
  }

  const handleReject = (id: string) => {
    setCurrentApplicationId(id)
    setReviewNotes('')
    setIsRejectVisible(true)
  }

  const confirmApprove = async () => {
    try {
      await approveTeacherApplication(currentApplicationId, { reviewNotes })
      message.success('审核通过成功')
      setIsApproveVisible(false)
      fetchData()
    } catch (err: unknown) {
      message.error((err as Error)?.message || '操作失败')
    }
  }

  const confirmReject = async () => {
    if (!reviewNotes.trim()) {
      message.error('请填写拒绝原因')
      return
    }
    try {
      await rejectTeacherApplication(currentApplicationId, { reviewNotes })
      message.success('审核拒绝成功')
      setIsRejectVisible(false)
      fetchData()
    } catch (err: unknown) {
      message.error((err as Error)?.message || '操作失败')
    }
  }

  const getStatusTag = (status: string) => {
    const statusMap: Record<string, { color: string; text: string }> = {
      PENDING: { color: 'orange', text: '待审核' },
      APPROVED: { color: 'green', text: '已通过' },
      REJECTED: { color: 'red', text: '已拒绝' },
    }
    const normalizedStatus = status.toUpperCase()
    const config = statusMap[normalizedStatus] || { color: 'default', text: status }
    return <Tag color={config.color}>{config.text}</Tag>
  }

  const columns = useRef<TableProps<ITeacherApplication>['columns']>([
    {
      title: '申请人',
      key: 'name',
      width: 150,
      fixed: 'left',
      render: (_, record) => `${record.firstName} ${record.lastName}`,
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      width: 200,
    },
    {
      title: '电话',
      dataIndex: 'phone',
      key: 'phone',
      width: 150,
    },
    {
      title: '国籍',
      dataIndex: 'nationality',
      key: 'nationality',
      width: 100,
    },
    {
      title: '学历',
      dataIndex: 'education',
      key: 'education',
      width: 120,
    },
    {
      title: '授课语言',
      key: 'teachingLanguage',
      width: 160,
      render: (_, record) => getTeachingLanguageSummary(record.languages, record.englishLevel),
    },
    {
      title: '时薪',
      dataIndex: 'hourlyRate',
      key: 'hourlyRate',
      width: 100,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => getStatusTag(status),
    },
    {
      title: '申请时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm'),
      width: 160,
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size={4}>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          >
            详情
          </Button>
          {admin && record.status.toUpperCase() === 'PENDING' && (
            <>
              <Button
                type="link"
                size="small"
                icon={<CheckOutlined />}
                onClick={() => handleApprove(record.id)}
              >
                通过
              </Button>
              <Button
                type="link"
                size="small"
                danger
                icon={<CloseOutlined />}
                onClick={() => handleReject(record.id)}
              >
                拒绝
              </Button>
            </>
          )}
        </Space>
      ),
      fixed: 'right',
      width: 240,
    },
  ])

  const onPageChange = useCallback((cPage: number, cSize: number) => {
    setPage(cPage)
    setSize(cSize)
  }, [])

  return (
    <Container>
      <Tabs
        defaultActiveKey="application"
        destroyInactiveTabPane={false}
        items={[
          {
            key: 'application',
            label: '第一步：资料审核',
            children: (
              <>
                <div className="top">
                  <Space>
                    <Select
                      placeholder="选择状态"
                      allowClear
                      value={status}
                      onChange={(value) => { setStatus(value || ''); setPage(1) }}
                      style={{ width: 120 }}
                      options={[
                        { value: 'PENDING', label: '待审核' },
                        { value: 'APPROVED', label: '已通过' },
                        { value: 'REJECTED', label: '已拒绝' },
                      ]}
                    />
                    <Button type="primary" loading={loading} onClick={onSearch}>
                      查询
                    </Button>
                  </Space>
                </div>
                <div className="table">
                  <Table
                    rowKey="id"
                    scroll={{ x: 1460, y: 510 }}
                    loading={loading}
                    columns={columns.current}
                    dataSource={dataSource}
                    pagination={{
                      total,
                      pageSize: size,
                      current: page,
                      onChange: onPageChange,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (value) => `共 ${value} 条`,
                    }}
                  />
                </div>
              </>
            ),
          },
          ...(admin ? [{
            key: 'demo',
            label: '第二步：Demo 课堂审核',
            children: <DemoReviewPanel admin={admin} />,
          }] : []),
        ]}
      />

      {/* 详情弹窗 */}
      <Modal
        title="教师申请详情"
        open={isDetailsVisible}
        onCancel={() => setIsDetailsVisible(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        {selectedApplication && (
          <ApplicationDetails application={selectedApplication} />
        )}
      </Modal>

      {/* 审核通过弹窗 */}
      <Modal
        title="审核通过"
        open={isApproveVisible}
        onOk={confirmApprove}
        onCancel={() => setIsApproveVisible(false)}
        okText="确认通过"
        cancelText="取消"
      >
        <div style={{ marginBottom: 16 }}>
          <Typography.Text>确认通过资料审核吗？老师可以登录完成 Demo 课堂，第二步审核通过后才能正式上课。</Typography.Text>
        </div>
        <TextArea
          placeholder="审核备注（可选）"
          value={reviewNotes}
          onChange={(e) => setReviewNotes(e.target.value)}
          rows={3}
        />
      </Modal>

      {/* 审核拒绝弹窗 */}
      <Modal
        title="审核拒绝"
        open={isRejectVisible}
        onOk={confirmReject}
        onCancel={() => setIsRejectVisible(false)}
        okText="确认拒绝"
        cancelText="取消"
        okButtonProps={{ danger: true }}
      >
        <div style={{ marginBottom: 16 }}>
          <Typography.Text strong>请填写拒绝原因：</Typography.Text>
        </div>
        <TextArea
          placeholder="请详细说明拒绝的原因"
          value={reviewNotes}
          onChange={(e) => setReviewNotes(e.target.value)}
          rows={4}
          required
        />
      </Modal>
    </Container>
  )
}

export default memo(Index)
